import { useEffect, useState } from "react";
import {
  getMyGroups,
  leaveGroup,
  deleteGroup,
  promoteToAdmin,
  demoteAdmin,
  transferGroupOwnership,
  removeGroupMember,
  addGroupMember,
} from "../../services/groupService";
import { getAllFriends } from "../../services/friendService";
import CreateGroupForm from "./CreateGroup";
import "./GroupList.css";

import { getCurrentUserId } from "../../utils/auth";

const GroupList = ({ token, onGroupSelect, unreadCounts }) => {
  const [groups, setGroups] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [friends, setFriends] = useState([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const currentUserId = getCurrentUserId();

  /* ================= HELPERS ================= */
  const getMe = (group) => {
    if (!currentUserId || !group?.participants) return null;

    return (
      group.participants.find((p) => p?.user?._id === currentUserId) || null
    );
  };

  const isOwner = (group) => getMe(group)?.role === "owner";
  const isAdmin = (group) => ["admin", "owner"].includes(getMe(group)?.role);

  /* ================= EFFECTS ================= */
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const res = await getAllFriends();
        setFriends(res.data || []);
      } catch (err) {
        console.error("Failed to fetch friends:", err);
      }
    };
    fetchFriends();
  }, [token]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getMyGroups();
        setGroups(res.data || []);
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }
    };
    fetchGroups();
  }, []);

  /* ================= ACTIONS ================= */
  const handleLeaveGroup = async (groupId) => {
    await leaveGroup(groupId);
    setGroups((prev) => prev.filter((g) => g._id !== groupId));
  };

  const handleDeleteGroup = async (groupId) => {
    await deleteGroup(groupId);
    setGroups((prev) => prev.filter((g) => g._id !== groupId));
  };

  const handlePromote = async (groupId, userId) => {
    await promoteToAdmin(groupId, userId);
    const updatedGroups = await getMyGroups();
    setGroups(updatedGroups.data || []);
  };

  const handleDemote = async (groupId, userId) => {
    await demoteAdmin(groupId, userId);
    const updatedGroups = await getMyGroups();
    setGroups(updatedGroups.data || []);
  };

  const handleTransferOwner = async (groupId, userId) => {
    await transferGroupOwnership(groupId, userId);
    const updatedGroups = await getMyGroups();
    setGroups(updatedGroups.data || []);
  };

  const handleRemove = async (groupId, userId) => {
    await removeGroupMember(groupId, userId);
    const updatedGroups = await getMyGroups();
    setGroups(updatedGroups.data || []);
  };

  const handleShowAddMemberModal = (groupId) => {
    setSelectedGroupId(groupId);
    setShowAddMemberModal(true);
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroupId) return;
    await addGroupMember(selectedGroupId, userId);
    const updatedGroups = await getMyGroups();
    setGroups(updatedGroups.data || []);
    setShowAddMemberModal(false);
  };

  /* ================= RENDER ================= */
  return (
    <div className="group-list">
      <h4>Groups</h4>

      {groups.length === 0 && !showCreateGroup && (
        <div className="empty-state">
          <p>No group chats yet? Join or create a group!</p>
        </div>
      )}

      <button onClick={() => setShowCreateGroup(true)}>Create Group</button>

      {showCreateGroup && (
        <CreateGroupForm
          friends={friends}
          onCreated={(g) => setGroups((prev) => [...prev, g])}
          onClose={() => setShowCreateGroup(false)}
        />
      )}

      {groups.map((group) => {
        const me = getMe(group);

        return (
          <div key={group._id} className="group-item">
            <div className="group-header">
              <span onClick={() => onGroupSelect(group)}>
                {group.name}
                {me?.role === "owner" && " (Owner)"}
                {me?.role === "admin" && " (Admin)"}
                {me?.role === "member" && " (Member)"}
              </span>

              {unreadCounts?.[group._id] > 0 && (
                <span className="badge">{unreadCounts[group._id]}</span>
              )}
            </div>

            {/* MEMBERS (ADMIN / OWNER ONLY) */}
            {isAdmin(group) && (
              <div className="member-list">
                <button
                  className="add-member-btn"
                  onClick={() => handleShowAddMemberModal(group._id)}
                >
                  + Add Member
                </button>

                {group.participants
                  ?.filter((p) => p?.user?._id)
                  .map((p) => (
                    <div key={p.user._id} className="member-row">
                      <span>
                        {p.user?.username || "Unknown"} ({p.role})
                      </span>

                      {isOwner(group) && p.role === "member" && p.user?._id && (
                        <button
                          onClick={() => handlePromote(group._id, p.user._id)}
                        >
                          Promote
                        </button>
                      )}

                      {isOwner(group) && p.role === "admin" && p.user?._id && (
                        <button
                          onClick={() => handleDemote(group._id, p.user._id)}
                        >
                          Demote
                        </button>
                      )}

                      {isOwner(group) && p.role !== "owner" && p.user?._id && (
                        <button
                          onClick={() =>
                            handleTransferOwner(group._id, p.user._id)
                          }
                        >
                          Make Owner
                        </button>
                      )}

                      {isAdmin(group) && p.role !== "owner" && p.user?._id && (
                        <button
                          onClick={() => handleRemove(group._id, p.user._id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {/* ACTIONS */}
            <div className="group-actions">
              <button onClick={() => onGroupSelect(group)}>Inbox</button>

              {me?.role !== "owner" && (
                <button onClick={() => handleLeaveGroup(group._id)}>
                  Leave
                </button>
              )}

              {isOwner(group) && (
                <button onClick={() => handleDeleteGroup(group._id)}>
                  Delete
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* ===== ADD MEMBER MODAL ===== */}
      {showAddMemberModal && (
        <div className="modal-backdrop">
          <div className="modal add-member-modal">
            <div className="modal-header">
              <h4>Add Members</h4>
              <button
                className="modal-close"
                onClick={() => setShowAddMemberModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="add-member-list">
              {friends
                .filter(
                  (f) =>
                    !groups
                      .find((g) => g._id === selectedGroupId)
                      ?.participants?.some((p) => p?.user?._id === f._id),
                )
                .map((f) => (
                  <div key={f._id} className="add-member-row">
                    <span>{f.username}</span>
                    <button onClick={() => handleAddMember(f._id)}>Add</button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupList;
